import { useEffect, useMemo, useState } from 'react'
import { PageHeader } from '../../../components/PageHeader'
import { WeeklySubmittedScreen } from '../../../components/WeeklySubmittedScreen'
import { BackgroundTitle } from '../../../components/BackgroundTitle'
import { COLORS, type ColorId } from '../data/colors'
import { ColorCardImage } from '../components/ColorCardImage'
import {
  ensureMyColorfulWeeklyEntry, saveColorfulWeeklyPick,
  type ColorfulWeeklyEntryRow, type ColorfulWeeklyEventRow,
  weekRangeLabel,
} from '../api/weekly'
import { supabase } from '../../../lib/supabase'
import { readError } from '../../../lib/errors'
import { useT } from '../../../i18n'

interface Props {
  eventId: string
  entryId: string
  onBack: () => void
}

/**
 * The player's 6-round picking screen for Colorful Weekly.
 *
 * One subject per round, the player taps a color card. Saves happen
 * per-pick so you can come back later and finish; the picks array on
 * the entry row is the source of truth.
 */
export function ColorfulWeeklyPlayScreen({ eventId, onBack }: Props) {
  const t = useT()
  const [event, setEvent] = useState<ColorfulWeeklyEventRow | null>(null)
  const [entry, setEntry] = useState<ColorfulWeeklyEntryRow | null>(null)
  const [round, setRound] = useState<number>(0)
  const [busy, setBusy] = useState(false)
  const [error, setError] = useState<string | null>(null)
  // Once all 6 are in we show the "submission received" screen; the
  // player can dip back into the form to tweak before the deadline.
  const [reviewing, setReviewing] = useState(false)

  useEffect(() => {
    let cancelled = false
    async function load() {
      try {
        const { data: ev, error: e1 } = await supabase
          .from('colorful_weekly_events').select('*').eq('id', eventId).single()
        if (e1) throw e1
        if (cancelled) return
        setEvent(ev as ColorfulWeeklyEventRow)

        // Refresh the entry — the caller might be re-entering after a
        // partial session and we want the latest picks from the DB.
        const ent = await ensureMyColorfulWeeklyEntry(eventId)
        if (cancelled) return
        setEntry(ent)
        // Jump to the first unanswered round.
        const firstUnanswered = ent.picks.findIndex(p => !p)
        if (firstUnanswered >= 0) setRound(firstUnanswered)
      } catch (e) {
        if (!cancelled) setError(readError(e))
      }
    }
    load()
    return () => { cancelled = true }
  }, [eventId])

  const subject = event?.subjects[round] ?? ''
  const myPick = entry?.picks[round] ?? null

  const answeredCount = useMemo(
    () => entry?.picks.filter(Boolean).length ?? 0,
    [entry],
  )

  async function pickColor(colorId: ColorId) {
    if (!entry) return
    if (busy) return
    setBusy(true); setError(null)
    try {
      const next = await saveColorfulWeeklyPick(entry.id, round, colorId)
      setEntry(next)
      // Advance to next unanswered round if any.
      const nextRound = next.picks.findIndex((p, i) => !p && i !== round)
      if (nextRound >= 0) setRound(nextRound)
    } catch (e) {
      setError(readError(e))
    } finally {
      setBusy(false)
    }
  }

  if (!event || !entry) {
    return (
      <div className="screen">
        <PageHeader title={t('Weekly · Colorful')} subtitle={t('Loading…')} onBack={onBack} />
        <div className="flex-1 px-5 py-5 space-y-3">
          <div className="h-40 rounded-2xl bg-brand-elevated animate-pulse" />
          <div className="h-24 rounded-2xl bg-brand-elevated animate-pulse" />
        </div>
      </div>
    )
  }

  // All 6 in → confirmation screen instead of sitting on the finished form.
  if (answeredCount === 6 && !reviewing) {
    return (
      <WeeklySubmittedScreen
        title={t('Weekly · Colorful')}
        accent="#74705F"
        message={t('All 6 colors are locked in. Results reveal next Monday. You can still tweak any pick until then.')}
        onBackToMenu={onBack}
        onReview={() => { setRound(0); setReviewing(true) }}
        reviewLabel={t('Review / edit my picks')}
      />
    )
  }

  return (
    <div className="screen">
      <BackgroundTitle text={t('Weekly Colorful')} color="#74705F" />
      <PageHeader
        title={t('Round {n} of 6', { n: round + 1 })}
        subtitle={t('{range} · {n} picked so far', {
          range: weekRangeLabel(event.week_start), n: answeredCount,
        })}
        onBack={reviewing ? () => setReviewing(false) : onBack}
      />

      <div className="flex-1 flex flex-col px-5 py-5 gap-5 overflow-y-auto pb-6 relative z-10">
        {error && (
          <div className="card p-3 bg-brand-danger/10 border border-brand-danger/30">
            <p className="text-sm text-brand-danger">{error}</p>
          </div>
        )}

        {/* Subject card */}
        <div className="card-lg p-6 bg-linear-to-br from-brand-text to-gray-800 text-white">
          <p className="text-xs font-semibold uppercase tracking-widest text-white/60 mb-2">
            {t('What color is')}
          </p>
          <p className="font-display text-3xl font-black">
            {subject}?
          </p>
        </div>

        {/* Round strip — jump between rounds */}
        <div className="flex gap-2">
          {event.subjects.map((_, i) => {
            const done = Boolean(entry.picks[i])
            const active = i === round
            return (
              <button
                key={i}
                onClick={() => setRound(i)}
                className={`flex-1 h-10 rounded-xl text-sm font-display font-bold transition-colors
                           ${active ? 'bg-brand-text text-white' :
                             done  ? 'bg-brand-primary/15 text-brand-text' :
                                     'bg-brand-elevated text-brand-muted'}`}
              >
                {i + 1}
                {done && !active && <span className="ml-1">✓</span>}
              </button>
            )
          })}
        </div>

        {/* Color grid */}
        <div>
          <p className="text-xs font-medium text-brand-muted uppercase tracking-wide mb-2">
            {t('Pick a color for "{subject}"', { subject })}
          </p>
          <div className="grid grid-cols-3 gap-2">
            {COLORS.map(c => {
              const selected = myPick === c.id
              return (
                <button
                  key={c.id}
                  onClick={() => pickColor(c.id)}
                  disabled={busy}
                  className="active:scale-95 transition-transform disabled:opacity-70"
                >
                  <span className="sr-only">{c.name}</span>
                  <ColorCardImage color={c.id} selected={selected} size="lg" />
                </button>
              )
            })}
          </div>
        </div>

        {answeredCount === 6 && (
          <div className="card p-4 bg-brand-success/10 border border-brand-success/30 text-center">
            <p className="font-semibold text-brand-text">{t('All 6 picks in.')}</p>
            <p className="text-sm text-brand-muted mt-1">{t('Results reveal next Monday. You can still tweak any pick until then.')}</p>
          </div>
        )}
      </div>
    </div>
  )
}
